import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Factory, PackageCheck, Check, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { PROJECTS_API, authHeaders, today } from './soldProjects';

const ICON = { ordered: Factory, ready: PackageCheck, installed: Check, edit: Pencil };
const COLOR = { ordered: 'bg-amber-500 hover:bg-amber-600', ready: 'bg-emerald-600 hover:bg-emerald-700', installed: 'bg-emerald-600 hover:bg-emerald-700', edit: 'bg-zinc-800 hover:bg-zinc-900' };

// mode: ordered (pedido feito) | ready (material chegou) | installed | edit (só dados do pedido)
export const SoldProjectStageDialog = ({ project, mode, onClose, onSaved, t }) => {
  const [form, setForm] = useState({ order_number: '', supplier: '', eta: '', received_at: '', notes: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (project) setForm({ order_number: project.order_number || '', supplier: project.supplier || '', eta: project.eta || '', received_at: project.received_at || (mode === 'ready' ? today() : ''), notes: project.notes || '' });
  }, [project, mode]);
  if (!project || !mode) return null;
  const Icon = ICON[mode];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const showOrder = mode !== 'installed';
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, ...(mode !== 'edit' ? { stage: mode } : {}) };
      const { data } = await axios.patch(`${PROJECTS_API}/${project.id}`, body, { headers: authHeaders() });
      toast.success(t.saved);
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t.error);
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="project-stage-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Icon className="h-4 w-4" /> {t.dlg[mode]} · <span className="font-normal text-zinc-600">{project.customer_name}</span></DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {showOrder && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>{t.dlg.orderNo}{mode === 'ordered' ? ' *' : ''}</Label><Input value={form.order_number} onChange={set('order_number')} required={mode === 'ordered'} data-testid="project-dlg-order" /></div>
                <div><Label>{t.dlg.supplier}</Label><Input value={form.supplier} onChange={set('supplier')} data-testid="project-dlg-supplier" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>{t.dlg.eta}</Label><Input type="date" value={form.eta} onChange={set('eta')} data-testid="project-dlg-eta" /></div>
                {(mode === 'ready' || project.received_at) && <div><Label>{t.dlg.received}</Label><Input type="date" value={form.received_at} onChange={set('received_at')} data-testid="project-dlg-received" /></div>}
              </div>
            </>
          )}
          <div><Label>{t.dlg.notes}</Label><Textarea rows={2} value={form.notes} onChange={set('notes')} data-testid="project-dlg-notes" /></div>
          {mode !== 'edit' && <p className="text-[11px] text-zinc-500">{t.dlg[`${mode}Hint`]}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} data-testid="project-dlg-cancel">{t.dlg.cancel}</Button>
            <Button type="submit" disabled={saving} className={`text-white ${COLOR[mode]}`} data-testid="project-dlg-save"><Icon className="h-3.5 w-3.5 mr-1" /> {t.dlg.save}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SoldProjectStageDialog;
